// src/pages/Settings.jsx
import { useState, useEffect } from 'react';
import { api } from '../api';
import { useApp } from '../AppContext';
import { Card, SectionTitle, PageHeader, Btn, Field, Input, Flash, Spinner } from '../components/UI';

const FEATURE_KEYS = [
  ['feature_signins',    'Sign-In Terminal', ''],
  ['feature_signin_log', 'Sign-In Log', ''],
  ['feature_parties',    'Party Bookings', ''],
  ['feature_reports',    'Manager Reports', ''],
  ['feature_codes',      'Code Generator', ''],
  ['feature_members',    'Member Status', ''],
  ['feature_guest_id',   'Guest ID Scanning', 'Requires USB PDF417 scanner. Off by default.'],
  ['feature_revenue',    'Show Revenue', 'Hides revenue totals from sign-in terminal when disabled.'],
];

const COLOR_KEYS = [
  ['color_accent',       'Accent Color'],
  ['color_accent_hover', 'Accent Hover'],
  ['color_nav_bg',       'Nav Background'],
  ['color_header_bg',    'Header Background'],
  ['color_header_text',  'Header Text'],
  ['color_success',      'Success Color'],
  ['color_warning',      'Warning Color'],
];

const PRICE_KEYS = [
  ['price_guest_cash',         'Guest — Cash'],
  ['price_guest_card',         'Guest — Card'],
  ['price_party_swim_cash',    'Party Swimmer — Cash'],
  ['price_party_swim_card',    'Party Swimmer — Card'],
  ['price_party_nonswim_cash', 'Party Non-Swimmer — Cash'],
  ['price_party_nonswim_card', 'Party Non-Swimmer — Card'],
  ['price_party_venue',        'Venue Fee (per 25 guests)'],
  ['price_charcoal',           'Charcoal Bag'],
];

export default function Settings() {
  const { refresh } = useApp();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [flash, setFlash]       = useState(null);

  useEffect(() => {
    api.get('/api/settings/get').then(r => {
      if (r?.ok) setSettings(r.data);
      setLoading(false);
    });
  }, []);

  function set(key, val) { setSettings(s => ({ ...s, [key]: val })); }

  async function save() {
    setSaving(true);
    const r = await api.post('/api/settings/save', settings);
    setSaving(false);
    if (r?.ok) { setFlash({ type: 'success', msg: 'Settings saved.' }); refresh(); }
    else setFlash({ type: 'error', msg: 'Failed to save.' });
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--fg3)' }}>Loading...</div>;
  if (!settings) return null;

  return (
    <div style={{ padding: '20px 16px', maxWidth: 860, margin: '0 auto' }}>
      <PageHeader title="Site Settings"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <a href={`${api.flaskUrl('/settings/roles')}`} style={{ padding: '7px 12px', fontSize: 12, fontWeight: 'bold', color: 'var(--accent)', border: '1px solid var(--accent)', textDecoration: 'none' }}>Manage Roles</a>
            <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</Btn>
          </div>
        }
      />

      {flash && <Flash type={flash.type} onDismiss={() => setFlash(null)}>{flash.msg}</Flash>}

      {/* Club Info */}
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle>Club Information</SectionTitle>
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[
            ['club_name',    'Club Name'],
            ['abbreviation', 'Abbreviation'],
            ['tagline',      'Tagline'],
          ].map(([key, label]) => (
            <Field key={key} label={label}>
              <Input value={settings[key] || ''} onChange={e => set(key, e.target.value)} />
            </Field>
          ))}
        </div>
      </Card>

      {/* Colors */}
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle>Colors</SectionTitle>
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {COLOR_KEYS.map(([key, label]) => (
            <Field key={key} label={label}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="color" value={settings[key] || '#000000'}
                  onChange={e => set(key, e.target.value)}
                  style={{ width: 36, height: 32, padding: 2, border: '1px solid var(--input-border)', background: 'var(--input-bg)', cursor: 'pointer' }} />
                <Input value={settings[key] || ''} onChange={e => set(key, e.target.value)} style={{ flex: 1 }} />
              </div>
            </Field>
          ))}
        </div>
      </Card>

      {/* Pricing */}
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle>Pricing</SectionTitle>
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {PRICE_KEYS.map(([key, label]) => (
            <Field key={key} label={label}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ padding: '8px 10px', background: 'var(--bg3)', border: '1px solid var(--input-border)', borderRight: 'none', color: 'var(--fg2)', fontSize: 13 }}>$</span>
                <Input type="number" step="0.01" min="0" value={settings[key] || ''} onChange={e => set(key, e.target.value)} style={{ borderLeft: 'none' }} />
              </div>
            </Field>
          ))}
        </div>
      </Card>

      {/* Features */}
      <Card style={{ marginBottom: 16 }}>
        <SectionTitle>Features</SectionTitle>
        <div style={{ padding: 16 }}>
          {FEATURE_KEYS.map(([key, label, hint]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-light)', cursor: 'pointer' }}>
              <input type="checkbox"
                checked={settings[key] !== 'off'}
                onChange={e => set(key, e.target.checked ? 'on' : 'off')}
                style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--accent)', flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 'bold', color: 'var(--fg)' }}>{label}</div>
                {hint && <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 2 }}>{hint}</div>}
              </div>
            </label>
          ))}
        </div>
      </Card>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Btn onClick={save} disabled={saving} style={{ padding: '10px 24px', fontSize: 14 }}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Btn>
      </div>
    </div>
  );
}
